/**
 * Audit-trail timeline — deterministic block classification + step mapping.
 *
 * Two pure concerns live here, both render-time only (no backend change, so
 * they apply retroactively to every already-persisted message):
 *
 * 1. **Narration routing.** Within one assistant message's content blocks, a
 *    `text` block with ANY `tool_call` block after it is *narration* — the
 *    model talking through its work. The trailing contiguous run of text
 *    blocks is the *answer*. Narration belongs in the audit timeline lane;
 *    only the answer belongs in the chat bubble.
 *
 * 2. **Step mapping.** A raw tool call ("Bash `ls /private/tmp/claude_harness/
 *    project/workdirs/1712/data/`") is turned into one semantic line
 *    ("Opened attached documents"). The mapping is a pure function of
 *    `(tool, arguments, result summary)` — there is never an LLM call here,
 *    and there never may be: the audit log has to be reproducible from the
 *    persisted blocks alone.
 *
 * Two hard guarantees the mapper owes the user, enforced by
 * `redactUnsafeLabel` on every label it emits:
 *   - hashed storage filenames (`000_mezz_all__4e2c58cc_....pdf`) never
 *     appear in a collapsed label;
 *   - absolute sandbox paths (`/tmp/claude_harness/...`) never appear either.
 * Neither guarantee is a display trick: the timeline has no expansion
 * affordance at all. A step is one line, and the raw call and result behind
 * it are not reachable from the panel — they stay persisted on the message
 * and flow to Langfuse, which is where a run gets debugged.
 */

import {
	BlockType,
	TraceType,
	type ContentBlock,
	type TraceBlock,
	type UIDocumentRef,
} from "../components/Chat/Chat.types";

/* ------------------------------------------------------------------ *
 * Narration routing
 * ------------------------------------------------------------------ */

const isToolCallBlock = (block: ContentBlock): block is TraceBlock =>
	block.type === BlockType.TRACE &&
	(block as TraceBlock).trace_type === TraceType.TOOL_CALL;

const isToolResultBlock = (block: ContentBlock): block is TraceBlock =>
	block.type === BlockType.TRACE &&
	(block as TraceBlock).trace_type === TraceType.TOOL_RESULT;

/**
 * True when the text block at `index` is interim narration rather than the
 * answer — i.e. some tool call still follows it.
 */
export const isNarrationTextBlock = (
	blocks: ContentBlock[],
	index: number,
): boolean => {
	const block = blocks[index];
	if (!block || block.type !== BlockType.TEXT) return false;
	for (let i = index + 1; i < blocks.length; i += 1) {
		if (isToolCallBlock(blocks[i])) return true;
	}
	return false;
};

/** The one arm that interleaves narration with its tool calls. */
export const CLAUDE_CHAT_TYPE = "claude_code";

/**
 * Whether narration routing applies to this chat at all.
 *
 * Only the Claude harness emits interim prose between tool calls; the other
 * arms emit one answer. Applying the rule to them would suppress a text-only
 * response for the whole turn — visible nowhere, since those arms have no
 * narration to put in the timeline lane either. So the routing is arm-scoped,
 * and every other arm renders exactly as it did before the flag existed.
 */
export const routesNarration = (
	chatType: string | null | undefined,
): boolean => chatType === CLAUDE_CHAT_TYPE;

export interface AnswerSelectionOptions {
	/**
	 * The chat's `chat_type`. Anything other than `claude_code` (including
	 * `undefined`, i.e. an arm we could not resolve) opts out of routing —
	 * the safe default is the pre-existing behaviour.
	 */
	chatType?: string | null;
	/**
	 * While a turn is still generating there is no way to know whether the
	 * text arriving now is the answer or narration that a later tool call
	 * will demote. Promoting it into the bubble and then yanking it into the
	 * sidebar is the one transition the user must never see, so during a
	 * stream ALL text stays in the timeline lane; the final classification
	 * promotes the trailing run into the bubble when the stream ends.
	 */
	isStreaming?: boolean;
}

/**
 * The blocks the chat bubble should render: everything except narration
 * text. Non-text blocks (artifacts, charts, workbooks) pass through
 * untouched — routing is a question about prose only.
 */
export const selectAnswerBlocks = (
	blocks: ContentBlock[],
	{ isStreaming = false, chatType }: AnswerSelectionOptions = {},
): ContentBlock[] => {
	if (!routesNarration(chatType)) return blocks;
	return blocks.filter((block, index) => {
		if (block.type !== BlockType.TEXT) return true;
		if (isStreaming) return false;
		return !isNarrationTextBlock(blocks, index);
	});
};

/* ------------------------------------------------------------------ *
 * Redaction guards
 * ------------------------------------------------------------------ */

/** `000_mezz_all__4e2c58cc_foo.xlsx` — the storage-hashed upload name. */
const HASHED_NAME_RE = /__[0-9a-f]{6,}/i;
/** Absolute sandbox roots the harness runs in. */
const ABSOLUTE_PATH_RE = /(?:^|[\s"'(=])\/(?:private\/)?(?:tmp|var|home|Users)\//;
/** Internal storage segments that must never surface in a label. */
const INTERNAL_SEGMENT_RE = /(?:workdirs|claude_harness|sandbox_sources)\//;
/** Any multi-segment path fragment — a label should read as prose. */
const PATH_FRAGMENT_RE = /(?:[\w.-]+\/){2,}/;
/**
 * Shell syntax that reached a label because the tool call looped over files
 * (`for f in …; do cat "$f"`) and the path matcher picked up the variable
 * rather than a filename. Live audit logs rendered "Read the $f email thread"
 * — a shell internal, in the customer trail this module exists to keep clean.
 *
 * The guards above are all shaped like paths, so none of them saw it. This one
 * is shaped like syntax: a sigil, a brace, a backtick, a pipe or a redirect.
 */
const SHELL_TOKEN_RE = /[$`|><]|\$\{|\breturn\b/;

/**
 * True when `text` carries anything the collapsed view is not allowed to
 * show. Exported so tests can assert the guarantee directly.
 */
export const containsUnsafeText = (text: string): boolean =>
	HASHED_NAME_RE.test(text) ||
	ABSOLUTE_PATH_RE.test(text) ||
	INTERNAL_SEGMENT_RE.test(text) ||
	PATH_FRAGMENT_RE.test(text) ||
	SHELL_TOKEN_RE.test(text);

/** Drop a candidate label fragment that would leak paths or hashes. */
const safeFragment = (text: string | null | undefined): string | null => {
	if (!text) return null;
	const trimmed = text.trim();
	if (!trimmed) return null;
	return containsUnsafeText(trimmed) ? null : trimmed;
};

/* ------------------------------------------------------------------ *
 * Step mapping
 * ------------------------------------------------------------------ */

export interface MappedStep {
	icon: string;
	/** Label as shown for a single occurrence. */
	label: string;
	/** Label as shown once collapsed (`×N`). Defaults to `label`. */
	pluralLabel?: string;
}

export interface StepMapperContext {
	/** Document references on the message, used to name attachments. */
	documentRefs?: UIDocumentRef[];
	/**
	 * The chat's `chat_type`. Only the Claude arm routes narration into the
	 * timeline lane; for every other arm the prose stays in the bubble, so
	 * emitting it here too would render it twice.
	 */
	chatType?: string | null;
}

/** Tools whose steps are pure plumbing and are hidden outright. */
const HIDDEN_TOOLS = new Set(["ToolSearch"]);

/** Tools whose arguments are inspected for filesystem signals. */
const PATH_TOOLS = new Set(["Bash", "Read", "Grep", "Glob"]);

/**
 * The line shown for anything the rule table does not name. Exported
 * because the timeline renders trace blocks it cannot map (phase banners,
 * unpaired results) as this same generic line — the audit log is a
 * customer-facing trail, so an unmapped step says "Analysis step" rather
 * than exposing the raw payload behind it.
 */
export const FALLBACK_STEP: MappedStep = { icon: "⚙️", label: "Analysis step" };

const asRecord = (value: unknown): Record<string, unknown> | null =>
	value && typeof value === "object" && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null;

/** Flatten every argument value into one searchable string. */
const argumentsText = (args: Record<string, unknown> | undefined): string => {
	if (!args) return "";
	return Object.values(args)
		.map((v) => (typeof v === "string" ? v : JSON.stringify(v) ?? ""))
		.join(" ");
};

const argString = (
	args: Record<string, unknown> | undefined,
	key: string,
): string | null => {
	const value = args?.[key];
	return typeof value === "string" && value.trim() ? value.trim() : null;
};

/**
 * Parse a tool-result summary as JSON. Harness results arrive either as a
 * bare JSON object or wrapped in the MCP `[{"type":"text","text":"{...}"}]`
 * envelope; both are unwrapped here.
 */
export const parseResultJson = (
	summary: string | undefined,
): Record<string, unknown> | null => {
	if (!summary) return null;
	let parsed: unknown;
	try {
		parsed = JSON.parse(summary);
	} catch {
		return null;
	}
	if (Array.isArray(parsed)) {
		const textPart = parsed.find(
			(item) =>
				asRecord(item)?.type === "text" &&
				typeof asRecord(item)?.text === "string",
		);
		const inner = asRecord(textPart)?.text;
		if (typeof inner !== "string") return null;
		try {
			return asRecord(JSON.parse(inner));
		} catch {
			return null;
		}
	}
	return asRecord(parsed);
};

/**
 * Read `"<key>": <n>` out of a result payload that may have been truncated
 * before it could parse. Only ever used as a fallback behind `parseResultJson`.
 */
const leadingCount = (summary: string | undefined, key: string): number | null => {
	if (!summary) return null;
	const match = new RegExp(`"${key}"\\s*:\\s*(\\d+)`).exec(summary);
	return match ? Number(match[1]) : null;
};

const num = (value: unknown): number | null =>
	typeof value === "number" && Number.isFinite(value) ? value : null;

const prettifyName = (raw: string): string =>
	raw
		.replace(/[_-]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();

const normalizeForMatch = (raw: string): string =>
	raw.toLowerCase().replace(/[^a-z0-9]/g, "");

const basename = (path: string): string => {
	const parts = path.split("/");
	return parts[parts.length - 1] ?? path;
};

/** Strip extension, the storage hash suffix, and the ordering prefix. */
const fileStem = (name: string): string =>
	basename(name)
		.replace(/\.[A-Za-z0-9]{1,5}$/, "")
		.split("__")[0]
		.replace(/^\d+[_-]/, "");

/**
 * Resolve a hashed sandbox filename back to the human name the user
 * uploaded, using the message's document refs. Ambiguous matches resolve to
 * `null` — a wrong name is worse than a generic one.
 */
export const resolveDisplayName = (
	path: string,
	refs: UIDocumentRef[] | undefined,
): string | null => {
	if (!refs?.length) return null;
	const stem = normalizeForMatch(fileStem(path));
	if (stem.length < 4) return null;
	const matches = new Set<string>();
	for (const ref of refs) {
		const filename = ref.filename;
		if (!filename) continue;
		const refStem = normalizeForMatch(fileStem(filename));
		if (refStem.length < 4) continue;
		if (refStem === stem || refStem.startsWith(stem) || stem.startsWith(refStem)) {
			matches.add(filename);
		}
	}
	if (matches.size !== 1) return null;
	const [only] = [...matches];
	return safeFragment(only);
};

/** First filesystem-looking token in the flattened arguments. */
const firstPathMatching = (text: string, marker: RegExp): string | null => {
	const tokens = text.split(/[\s"'`,()\[\]{}]+/);
	for (const token of tokens) {
		if (marker.test(token)) return token;
	}
	return null;
};

const SQL_TABLE_RE = /\b(?:from|join)\s+([a-z_][a-z0-9_.]*)/gi;

const sqlTableNames = (text: string): string[] => {
	const names = new Set<string>();
	for (const match of text.matchAll(SQL_TABLE_RE)) {
		const name = match[1];
		if (!name || name.includes("/")) continue;
		names.add(name);
	}
	return [...names];
};

/**
 * The per-chat workdir the harness stages uploads into. Matched with no
 * trailing slash so a `cd`-into-the-directory call counts as opening the
 * documents it then reads.
 */
const WORKDIR_DATA_RE = /workdirs\/\d+\/data(?![\w-])/;

const mapPathStep = (
	block: TraceBlock,
	context: StepMapperContext,
): MappedStep | null => {
	const text = argumentsText(block.arguments);

	// Attached documents — the per-chat workdir the harness stages uploads in.
	// The path is as often `cd …/workdirs/1758/data && pdftotext 028_*.pdf` as
	// it is a full file path, so the marker must not require a trailing slash:
	// requiring one sent every `cd`-then-read call to the generic step.
	if (WORKDIR_DATA_RE.test(text)) {
		const path = firstPathMatching(text, WORKDIR_DATA_RE);
		const name = path ? resolveDisplayName(path, context.documentRefs) : null;
		return name
			? { icon: "📎", label: `Opened ${name}` }
			: {
					icon: "📎",
					label: "Opened attached document",
					pluralLabel: "Opened attached documents",
				};
	}

	// Deal-room documents — `.../deal_rooms/<client>/deals/<Deal>/...`.
	if (/deal_rooms\//.test(text)) {
		const dealMatch = text.match(/deals\/([^/\s"'`]+)/);
		const deal = dealMatch ? safeFragment(prettifyName(dealMatch[1])) : null;
		const path = firstPathMatching(text, /deal_rooms\//);
		const name = path ? resolveDisplayName(path, context.documentRefs) : null;
		const what = name ?? "deal-room document";
		return {
			icon: "📄",
			label: deal ? `Read ${deal}: ${what}` : `Read ${what}`,
		};
	}

	// Synthetic connector sources staged for the demo account.
	if (/sandbox_sources\//.test(text)) {
		const lowered = text.toLowerCase();
		if (lowered.includes("dealcloud")) {
			const tables = sqlTableNames(text)
				.map((t) => safeFragment(t))
				.filter((t): t is string => Boolean(t));
			return {
				icon: "🗄️",
				label: tables.length
					? `Queried DealCloud CRM (${tables.join(", ")})`
					: "Queried DealCloud CRM",
			};
		}
		if (lowered.includes("email")) {
			const path = firstPathMatching(text, /sandbox_sources\//);
			const stem = path ? fileStem(path) : "";
			const deal = safeFragment(
				prettifyName(stem.replace(/[_-]?(thread|threads|emails?|inbox)$/i, "")),
			);
			return {
				icon: "✉️",
				label: deal ? `Read the ${deal} email thread` : "Read the email thread",
			};
		}
		if (lowered.includes("slack")) {
			const channel =
				argString(block.arguments, "channel") ??
				text.match(/#([a-z0-9_-]+)/i)?.[1] ??
				(() => {
					const path = firstPathMatching(text, /sandbox_sources\/[^\s]*slack/i);
					const stem = path ? fileStem(path) : "";
					return stem || null;
				})();
			const safeChannel = channel ? safeFragment(prettifyName(channel)) : null;
			return {
				icon: "💬",
				label: safeChannel ? `Searched Slack (#${safeChannel})` : "Searched Slack",
			};
		}
		if (lowered.includes("portfolio")) {
			return { icon: "📁", label: "Read the portfolio rollup" };
		}
	}

	return null;
};

/**
 * Structured breadcrumb for tool calls that fell through to the generic
 * label. The audit mapper is exhaustive by construction, so a gap means the
 * harness grew a tool the table does not know about — that has to be
 * discoverable without reading transcripts. Arguments are deliberately NOT
 * logged (they carry sandbox paths); the shape is enough to close the gap.
 */
const logMapperGap = (call: TraceBlock, reason: string): void => {
	console.debug("audit-mapper gap", {
		reason,
		tool: call.tool ?? null,
		arg_keys: call.arguments ? Object.keys(call.arguments).sort() : [],
		trace_type: call.trace_type,
	});
};

const mapToolStepInner = (
	call: TraceBlock,
	result: TraceBlock | undefined,
	context: StepMapperContext,
): MappedStep | null => {
	const tool = call.tool ?? "";
	if (HIDDEN_TOOLS.has(tool)) return null;

	if (tool === "Skill") {
		const skill = safeFragment(argString(call.arguments, "skill"));
		return {
			icon: "🧭",
			label: skill
				? `Applied the ${prettifyName(skill)} playbook`
				: "Applied a playbook",
		};
	}

	if (tool === "mcp__doppler__submit_cited_values") {
		const payload = parseResultJson(result?.summary);
		const counts = asRecord(payload?.counts);
		const verifiedList = payload?.verified;
		const rejectedList = payload?.rejected;
		const verified =
			num(counts?.verified) ??
			(Array.isArray(verifiedList) ? verifiedList.length : null);
		const rejected =
			num(counts?.rejected) ??
			(Array.isArray(rejectedList) ? rejectedList.length : null);
		if (verified === null) return { icon: "✅", label: "Verified values" };
		const total = verified + (rejected ?? 0);
		return { icon: "✅", label: `Verified ${verified}/${total} values` };
	}

	if (
		tool === "mcp__doppler__cite_pdf" ||
		tool === "mcp__doppler__cite_source"
	) {
		// Both citation tools return the same thin envelope: `accepted` plus the
		// `recorded` echo the model reads back. This is the step every evidence
		// row on the message hangs under, so it has to be named, not generic.
		const payload = parseResultJson(result?.summary);
		const recorded = payload?.recorded;
		const accepted =
			num(payload?.accepted) ??
			(Array.isArray(recorded) ? recorded.length : null) ??
			// A long citation call's result is persisted truncated, so it no
			// longer parses as JSON. `accepted` is the envelope's first key and
			// survives the cut — read it off the text rather than dropping the
			// count the reader came for.
			leadingCount(result?.summary, "accepted");
		return {
			icon: "\u2705",
			label:
				accepted === null
					? "Recorded citations"
					: `Recorded ${accepted} citation${accepted === 1 ? "" : "s"}`,
		};
	}

	if (tool === "mcp__doppler__extract_with_citations") {
		const payload = parseResultJson(result?.summary);
		const coverage = asRecord(payload?.coverage);
		const filled = num(coverage?.filled);
		const total = num(coverage?.total);
		const title = safeFragment(argString(call.arguments, "title"));
		const counted =
			filled !== null && total !== null
				? `Extracted ${filled}/${total} values`
				: "Extracted values";
		return { icon: "✅", label: title ? `${counted} — ${title}` : counted };
	}

	if (tool === "mcp__doppler__manipulate_workbook") {
		const detail = safeFragment(
			argString(call.arguments, "title") ??
				argString(call.arguments, "description") ??
				argString(call.arguments, "task"),
		);
		return {
			icon: "📊",
			label: detail
				? `Ran a workbook operation — ${detail}`
				: "Ran a workbook operation",
		};
	}

	if (tool === "mcp__doppler__create_chart") {
		return { icon: "📈", label: "Built a chart" };
	}

	if (tool === "mcp__doppler__web_search") {
		const query = safeFragment(
			argString(call.arguments, "query") ?? argString(call.arguments, "task"),
		);
		return {
			icon: "🌐",
			label: query ? `Searched the web — ${query}` : "Searched the web",
		};
	}

	if (PATH_TOOLS.has(tool)) {
		const step = mapPathStep(call, context);
		if (step) return step;
	}

	logMapperGap(call, "unmapped_tool");
	return FALLBACK_STEP;
};

/**
 * Map one tool call (plus its result, when the pairing is known) to the
 * single line the audit timeline shows. Returns `null` for steps that are
 * hidden outright.
 *
 * Every label leaves through this function's redaction check, so a future
 * rule that forgets `safeFragment` still cannot leak a path or a hash — it
 * degrades to the generic step and logs a gap.
 */
export const mapToolStep = (
	call: TraceBlock,
	result: TraceBlock | undefined,
	context: StepMapperContext = {},
): MappedStep | null => {
	const step = mapToolStepInner(call, result, context);
	if (!step) return null;
	if (containsUnsafeText(step.label)) {
		logMapperGap(call, "unsafe_label");
		return FALLBACK_STEP;
	}
	return step;
};

/* ------------------------------------------------------------------ *
 * Timeline assembly
 * ------------------------------------------------------------------ */

export interface NarrationItem {
	kind: "narration";
	key: string;
	text: string;
}

export interface StepItem {
	kind: "step";
	key: string;
	icon: string;
	/** Label as rendered — pluralized once the row collapses several steps. */
	label: string;
	/**
	 * The singular label, kept as the collapse identity. `label` may have
	 * been swapped for the plural form, so comparing it would break the
	 * run after the first merge.
	 */
	baseLabel: string;
	/** How many identical steps this row collapses. */
	count: number;
	/**
	 * The call, and the result it was paired with. Not rendered — the panel
	 * shows one line per step — but load-bearing: the result's payload is
	 * where a label's counts come from ("Extracted 240/252 values"), so this
	 * is also the seam the pairing tests assert against.
	 */
	blocks: TraceBlock[];
}

/**
 * A trace block that is not a tool call/result (phase banners, errors,
 * evidence metadata). The mapper has nothing to say about these, so they
 * pass through to the existing trace registry unchanged.
 */
export interface RawItem {
	kind: "raw";
	key: string;
	block: TraceBlock;
}

export type AuditTimelineItem = NarrationItem | StepItem | RawItem;

/**
 * Map each tool call's block index to the index of the result answering it.
 *
 * Two disjoint mechanisms, and a block belongs to exactly one of them:
 *
 * **By `tool_use_id`** — exact. The Claude arm fans out tool calls, so
 * `call A, call B, result B, result A` is an ordinary trajectory and any
 * positional rule gets it backwards.
 *
 * **By per-tool FIFO queue** — the fallback for emitters that supply no id
 * (and for messages persisted before the id survived replay). The nth
 * id-less call of a tool pairs with the nth id-less result of that tool,
 * which is the only rule that survives `call A, call B, result A, result B`:
 * a forward scan that stops at the next call leaves A unpaired and then
 * hands A's result to B, cross-attributing one call's output to another.
 *
 * ID-bearing blocks never enter the queues, so an id-less call can never
 * consume a result that demonstrably belongs to a parallel call, and vice
 * versa. A result no call claims is left unpaired and stays visible as a raw
 * row — failing to pair costs a folded row, mis-pairing would put one call's
 * output under another's label.
 *
 * Duplicates are refused on both paths: a result already claimed by one call
 * is never handed to a second, so a repeated `tool_use_id` (a contract
 * violation) degrades to one paired step plus a visible orphan rather than
 * to the same output shown under two labels.
 */
const pairCallsToResults = (blocks: ContentBlock[]): Map<number, number> => {
	const resultIndexById = new Map<string, number>();
	for (let i = 0; i < blocks.length; i += 1) {
		const block = blocks[i];
		if (!isToolResultBlock(block)) continue;
		const id = block.tool_use_id;
		if (!id || resultIndexById.has(id)) continue;
		resultIndexById.set(id, i);
	}

	const pairs = new Map<number, number>();
	const claimed = new Set<number>();
	// tool name → indices of id-less calls still waiting for a result.
	const waiting = new Map<string, number[]>();

	for (let i = 0; i < blocks.length; i += 1) {
		const block = blocks[i];
		if (block.type !== BlockType.TRACE) continue;
		const trace = block as TraceBlock;
		const id = trace.tool_use_id;
		const tool = trace.tool ?? "";

		if (isToolCallBlock(trace)) {
			if (id) {
				const exact = resultIndexById.get(id);
				if (exact !== undefined && !claimed.has(exact)) {
					claimed.add(exact);
					pairs.set(i, exact);
				}
				continue;
			}
			const queue = waiting.get(tool);
			if (queue) queue.push(i);
			else waiting.set(tool, [i]);
			continue;
		}

		if (isToolResultBlock(trace) && !id) {
			const callIndex = waiting.get(tool)?.shift();
			if (callIndex !== undefined) {
				claimed.add(i);
				pairs.set(callIndex, i);
			}
		}
	}

	return pairs;
};

/**
 * Build the ordered timeline for one assistant message: narration beats
 * interleaved at their true position between semantic tool steps.
 */
export const buildAuditTimeline = (
	blocks: ContentBlock[],
	context: StepMapperContext = {},
): AuditTimelineItem[] => {
	const items: AuditTimelineItem[] = [];
	const consumedResults = new Set<number>();
	const resultIndexForCall = pairCallsToResults(blocks);

	const pushStep = (step: MappedStep, raw: TraceBlock[], key: string): void => {
		const previous = items[items.length - 1];
		if (
			previous &&
			previous.kind === "step" &&
			previous.icon === step.icon &&
			previous.baseLabel === step.label
		) {
			previous.count += 1;
			previous.label = step.pluralLabel ?? step.label;
			previous.blocks.push(...raw);
			return;
		}
		items.push({
			kind: "step",
			key,
			icon: step.icon,
			label: step.label,
			baseLabel: step.label,
			count: 1,
			blocks: raw,
		});
	};

	for (let i = 0; i < blocks.length; i += 1) {
		const block = blocks[i];

		if (block.type === BlockType.TEXT) {
			// Non-Claude arms keep their prose in the bubble, so putting it in
			// the lane as well would show the same text twice.
			if (!routesNarration(context.chatType)) continue;
			if (!isNarrationTextBlock(blocks, i)) continue;
			const text = block.text.trim();
			if (text) items.push({ kind: "narration", key: `narration-${i}`, text });
			continue;
		}

		if (block.type !== BlockType.TRACE) continue;
		const trace = block as TraceBlock;

		if (isToolCallBlock(trace)) {
			const resultIndex = resultIndexForCall.get(i);
			const result =
				resultIndex === undefined
					? undefined
					: (blocks[resultIndex] as TraceBlock);
			if (resultIndex !== undefined) consumedResults.add(resultIndex);
			const step = mapToolStep(trace, result, context);
			if (!step) continue;
			const raw = result ? [trace, result] : [trace];
			pushStep(step, raw, `step-${i}`);
			continue;
		}

		if (isToolResultBlock(trace)) {
			// Paired results already belong to their step. An unpaired one (its
			// call was hidden, or never emitted) has no semantic line to sit
			// under, so it becomes a generic step rather than vanishing from
			// the audit log — the trail should not skip work that happened.
			if (consumedResults.has(i)) continue;
			items.push({ kind: "raw", key: `raw-${i}`, block: trace });
			continue;
		}

		items.push({ kind: "raw", key: `raw-${i}`, block: trace });
	}

	return items;
};

/**
 * How many steps the audit log will actually LIST for a message.
 *
 * The collapsed pill and the open panel used to count separately: the pill
 * counted raw trace blocks, the panel counted timeline items with narration
 * filtered out and identical consecutive steps collapsed into one `×N` row. On
 * a real answer that read "Audit log · 29 steps" on the outside and "25 steps"
 * on the inside — of the same message.
 *
 * A number that contradicts itself is bad anywhere and disqualifying in an
 * audit trail, so both now call this. The panel's reading is the correct one:
 * the count has to be what a reader can point at.
 */
export const auditStepCount = (
	blocks: ContentBlock[],
	context: StepMapperContext = {},
): number =>
	buildAuditTimeline(blocks, context).filter((item) => item.kind !== "narration")
		.length;

/* ------------------------------------------------------------------ *
 * The badge both surfaces render
 * ------------------------------------------------------------------ */

export interface AuditLogCounts {
	/** Steps the panel lists, including the reader's own column inspections. */
	steps: number;
	/** Evidence source rows the panel lists under those steps. */
	evidence: number;
}

export interface AuditLogCountOptions extends StepMapperContext {
	/** Evidence events on the message. Every one of them is rendered. */
	evidenceCount?: number;
	/** Session-only column-lineage inspections; they render as steps. */
	inspectionCount?: number;
}

/**
 * The counts shown on the collapsed pill AND in the open drawer's header.
 *
 * `auditStepCount` unified how a STEP is counted; this unifies what the two
 * surfaces count at all. The pill was still reaching the timeline count only
 * for the Claude arm (`routesNarration`), while the panel built the timeline
 * for every arm — so on a message whose `chat_type` had not loaded yet the
 * pill fell back to counting raw trace blocks and read "3 steps" beside a
 * drawer headed "1 step". One function, one answer, both surfaces.
 */
export const auditLogCounts = (
	blocks: ContentBlock[],
	{ evidenceCount = 0, inspectionCount = 0, ...context }: AuditLogCountOptions = {},
): AuditLogCounts => ({
	steps: auditStepCount(blocks, context) + inspectionCount,
	evidence: evidenceCount,
});

/**
 * "2 steps · 22 evidence" — the one label. A zero part is omitted rather than
 * shown as "0", and an empty log renders no badge at all.
 */
export const formatAuditLogCounts = ({ steps, evidence }: AuditLogCounts): string => {
	const parts: string[] = [];
	if (steps > 0) parts.push(`${steps} ${steps === 1 ? "step" : "steps"}`);
	if (evidence > 0) parts.push(`${evidence} evidence`);
	return parts.join(" · ");
};
